import React, { useState, useEffect } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { securePut } from '../../config/api';
import { useTranslation } from 'react-i18next';
import { Helmet } from "react-helmet-async";
import Input from '../../components/Input';
import { formatAmount } from "../../utils/format";
import { SaveCancel } from '../../components/PrimaryButton';
import SnackbarNotification from '../../components/SnackbarNotification';
import Breadcrumbs from '../../components/Breadcrumbs';

import plusIcon from '../../icons/plus.svg';
import DeleteIcon from '../../icons/trashR.svg';
import noImage from '../../icons/noImageIcon.svg';
import plus from '../../icons/plusP.svg';
import minus from '../../icons/minusP.svg';

const AvoirFournisseurForm = ({ title, baseFields = [], listFields = [], onSuccess, onCancel, products = [] }) => {
  const { id } = useParams();
  const history = useHistory();
  const { t } = useTranslation();

  const [formValues, setFormValues] = useState({});
  const [listItems, setListItems] = useState([]);
  const [loading, setLoading] = useState(!!id);
  const [baseFieldValues, setBaseFieldValues] = useState({});
  const [newListItemValues, setNewListItemValues] = useState({});
  const [totalTTC, setTotalTTC] = useState(0);

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  useEffect(() => {
    const calculateTotal = () => {
      return listItems.reduce((acc, item) => {
        const price = Number(item.priceuHT) || 0;
        const quantity = Number(item.quantity) || 0;
        return acc + (price * quantity);
      }, 0);
    };

    setTotalTTC(calculateTotal());
  }, [listItems]);


  const showSnackbar = (message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const requiredFields = [...baseFields, ...listFields];
    const missing = requiredFields.filter(f => f.required && !formValues[f.name]);
    if (missing.length > 0) {
        showSnackbar(`Veuillez remplir les champs obligatoires : ${missing.map(f => f.label).join(', ')}`, 'error');
        return;
    }


    const payload = {
      supplierId: Number(formValues.provider),
      details: listItems.map(item => ({
        inventoryId: Number(formValues.repo),
        priceTTC: Number(item.priceuHT),
        productId: Number(item.product_id),
        quantity: Number(item.quantity)
      }))
    };


    try {
      const response = await securePut("/avoirsFournisseur/add", payload, showSnackbar);
      onSuccess();
      history.push("/avoirs-fournisseurs");
    } catch (error) {
      console.error('Submission error:', error);
    }
  };

  const removeListItem = (index) => {
    const updatedItems = [...listItems];
    updatedItems.splice(index, 1);
    setListItems(updatedItems);
  };

  const addListItem = () => {
    if (Object.values(newListItemValues).every(val => val !== undefined && val !== '')) {
      const newItem = {
        product_id: Number(newListItemValues.product),
        quantity: Number(newListItemValues.quantity) || 1,
        priceuHT: Number(newListItemValues.priceuHT) || 0,
        ...baseFieldValues
      };


      setListItems(prev => [...prev, newItem]);
      setNewListItemValues({});
    }
  };

  const handleQuantityChange = (index, newQuantity) => {
    setListItems(prevItems => {
      const updatedItems = [...prevItems];
      updatedItems[index].quantity = Math.max(1, Number(newQuantity));
      return updatedItems;
    });
  };

  const handlePriceChange = (index, value) => {
    setListItems(prevItems => {
      const updatedItems = [...prevItems];
      const prev = String(updatedItems[index].priceuHT ?? "0");
      let next = normalizeDecimalInput(value);
      if (prev === "0") {
        next = normalizeDecimalInput(value);
      }
      updatedItems[index].priceuHT = next;
      return updatedItems;
    });
  };
  const finalizePrice = (index) => {
    setListItems(prevItems => {
      const updated = [...prevItems];
      let v = String(updated[index].priceuHT ?? "");
      if (v === "" || v === ".") v = "0";
      if (v.endsWith(".")) v = v.slice(0, -1);
      updated[index].priceuHT = v;
      return updated;
    });
  };

  const normalizeDecimalInput = (raw) => {
    if (raw == null) return "0";
    let s = String(raw);

    s = s.replace(/,/g, ".");

    s = s.replace(/[^\d.]/g, "");

    const firstDot = s.indexOf(".");
    if (firstDot !== -1) {
      s = s.slice(0, firstDot + 1) + s.slice(firstDot + 1).replace(/\./g, "");
    }

    if (s.startsWith(".")) s = "0" + s;

    if (s.includes(".")) {
      let [int, dec] = s.split(".");
      int = int.replace(/^0+(?=\d)/, "");
      if (int === "") int = "0";
      s = dec !== undefined ? `${int}.${dec}` : int;
    } else {
      s = s.replace(/^0+(?=\d)/, "");
      if (s === "") s = "0";
    }

    return s;
  };

  const handleImageError = (e) => {
    e.target.src = noImage;
    e.target.style.width = "25px";
    e.target.style.height = "25px";
    e.target.style.objectFit = "contain";
  };

  if (loading) return <div>{t('common.loading')}</div>;

  return (
    <div className="">
      <Helmet>
        <title>GrayFox - {t("titles.afournisseur")}</title>
      </Helmet>
      <Breadcrumbs />

      <h2 className="text-[32px] text-[#38485C] font-semibold mb-4">{title}</h2>

      <form className="space-y-4">

        <div className="space-y-4">
          <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
            {t('fields_types.products').toUpperCase()}
          </h3>

          <div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
              {[...baseFields, ...listFields].map((field, fieldIndex) => {
                const isBaseField = baseFields.some(bf => bf.name === field.name);
                const value = isBaseField
                  ? baseFieldValues[field.name] || ""
                  : newListItemValues[field.name] || "";

                const onChange = (value) => {
                  setFormValues(prev => ({ ...prev, [field.name]: value }));
                  if (isBaseField) {
                    setBaseFieldValues(prev => ({ ...prev, [field.name]: value }));
                  } else {
                    setNewListItemValues(prev => ({ ...prev, [field.name]: value }));
                  }
                };


                return (
                  <Input
                    key={fieldIndex}
                    type={field.type || "text"}
                    label={field.label}
                    value={value}
                    onChange={onChange}
                    required={field.required}
                    options={field.options || []}
                  />
                );
              })}


              <button
                type="button"
                onClick={addListItem}
                className="bg-[#043F88] text-white w-[48px] h-[48px] rounded-md hover:bg-[#3F3456] flex items-center justify-center"
              >
                <img src={plusIcon} alt="Add" className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {/* List Section for existing items (when modifying or adding) */}
        {listItems.length > 0 && (
          <div className="space-y-6">
            <h3 className="text-[16px] font-semibold text-[#ACAFC6]">
              {t('fields_types.liste').toUpperCase()}
            </h3>
            <div className="space-y-2">
              {listItems.map((item, index) => {
                const product = products.find(p => p.value === item.product_id);

                return (
                  <div key={index} className="grid grid-cols-[30%_20%_20%_20%_10%] items-center bg-white p-4 rounded-[12px] shadow-[0px_6px_26px_rgba(98,107,125,0.07)]">
                    <div className="flex items-center gap-2">
                      <div className="w-[76px] h-[76px] flex items-center justify-center  shadow-[0px_6px_26px_rgba(98,107,125,0.07)] rounded-[12px] overflow-hidden">
                        {product?.fullEntity?.image ? (
                          <img
                            src={product?.fullEntity?.image}
                            alt={product?.name}
                            className="w-full h-full object-contain"
                            onError={handleImageError}
                          />
                        ) : (
                          <div className="w-full h-full bg-white"></div>
                        )}
                      </div>
                      <div>
                        <p className="text-[#043F88] font-semibold text-[12px]">
                          {product?.reference}
                        </p>
                        <p className="text-secondary font-semibold text-[16px]">
                          {product?.name}
                        </p>
                      </div>
                    </div>

                    <div className="text-center">
                      <p className="text-[#707070] text-[12px] font-semibold">
                        {t('rows.priceuHT').toUpperCase()}
                      </p>
                      <Input
                        type="text"
                        inputMode="decimal"
                        pattern="[0-9]*[.,]?[0-9]*"
                        className="text-[#043F88] text-[20px] font-medium text-center border border-gray-300 rounded-md px-2 py-1 w-full"
                        value={item.priceuHT}
                        isNormal={true}
                        onChange={(value) => handlePriceChange(index, value)}
                        onFocus={(e) => {
                          if (e.target.value === "0") {
                            e.target.select(); 
                          }
                        }}
                        onBlur={() => finalizePrice(index)}
                      />

                    </div>

                    {/* Quantité */}
                    <div className="flex flex-col items-center">
                      <p className="text-[#707070] font-semibold text-[12px] mb-1">{t(`rows.quantity`).toUpperCase()}</p>
                      <div className="flex items-center rounded-[12px] border border-secondary bg-secondary/20 divide-x divide-secondary">
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(index, Math.max(item.quantity - 1, 1))}
                          className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold"
                        >
                          <img src={minus} alt="Minus" className="w-4 h-4" />
                        </button>
                        <span className="w-10 text-center font-semibold text-[#38485C] text-[14px]">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleQuantityChange(index, item.quantity + 1)}
                          className="w-8 h-8 flex items-center justify-center text-[#6C78F8] font-bold"
                        >
                          <img src={plus} alt="Plus" className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="text-center">
                      <p className="text-[#707070] text-[12px] font-semibold">
                        Total TTC
                      </p>
                      <p className="text-[#043F88] text-[20px] font-medium">
                        {formatAmount(item.priceuHT * item.quantity) || 0}
                      </p>
                    </div>

                    <button
                      type="button"
                      onClick={() => removeListItem(index)}
                      className="w-8 h-8 flex items-center justify-center bg-[#F9FAFC] rounded-[12px] border border-[#EF466F4D]"
                    >
                      <img src={DeleteIcon} alt="Delete" className="w-4 h-4" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        )}


        {/* Form Actions */}
        <div className="flex justify-between items-center mt-6">
          <div className="flex text-left">
            <p className="text-[#231E29] text-[16px] font-medium">
              TOTAL TTC:
            </p>
            <p className="text-[#043F88] text-[16px] font-bold pl-2">
              {formatAmount(totalTTC)}
            </p>
          </div>
          <div className="space-x-4">
            <SaveCancel
              onSaveClick={(e) => handleSubmit(e)}
              onCancelClick={onCancel}
            />
          </div>
        </div>
      </form>

      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(prev => ({ ...prev, open: false }))}
      />
    </div>
  );
};

export default AvoirFournisseurForm;
